import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["accessibilityLabel", "arrow", "disabled", "error", "fieldTooltip", "fieldTooltipAriaLabel", "fieldTooltipFluidWidth", "ids", "hasValue", "label", "loading", "name", "onActive", "onChange", "onDisabledClick", "options", "selectedOption", "selectRef", "styleSheet", "value", "warning", "UNSAFE_containerProps", "UNSAFE_selectProps"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import PropTypes from 'prop-types';
import omitProps from '@hocs/omit-props';
import { compose, setDisplayName, setPropTypes } from 'recompose';
import { withUncontrolledProp } from '@klarna/higher-order-components';
import wrapOverridableComponent from '@bubble-ui/hoc-wrap-overridable-component';
import { withDesignVersion } from '@bubble-ui/provider-design-version';
import { withDesignContext } from '@klarna/designtokens';
import defaultColorVariables from '@bubble-ui/particle-default-color-variables';
import defaultTextStyles from '@bubble-ui/particle-default-text-styles';
import ActionIconChevronBottom from '@bubble-ui/icon-action-chevron-bottom';
import Loader from '@bubble-ui/molecule-loader';
import baseDropdown from '@bubble-ui/temp-base-dropdown';
import FieldTooltipParticle from '@bubble-ui/temp-field-tooltip';
import LabelParticle from '@bubble-ui/atom-label';
import View from '@bubble-ui/primitive-view';
import withDeprecationWarning from '@bubble-ui/hoc-with-deprecation-warning';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
var BorderlessDropdown = _ref => {
  var {
      accessibilityLabel,
      arrow,
      disabled,
      error,
      fieldTooltip,
      fieldTooltipAriaLabel,
      fieldTooltipFluidWidth,
      ids,
      hasValue,
      label,
      loading,
      name,
      onActive,
      onChange,
      onDisabledClick,
      options,
      selectedOption,
      selectRef,
      styleSheet: {
        rootStyle,
        arrowStyle,
        containerStyle,
        backgroundStyle,
        textWrapperStyle,
        labelViewStyle,
        labelTextStyle,
        selectedOptionStyle,
        selectStyle,
        loaderStyle,
        iconStyle,
        labelFeedbackStateIconStyle
      },
      value,
      warning,
      UNSAFE_containerProps,
      UNSAFE_selectProps
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  var selectElement = /*#__PURE__*/React.createElement("select", _extends({
    id: ids.select || name
  }, UNSAFE_selectProps, {
    name: name,
    ref: selectRef,
    style: selectStyle,
    disabled: disabled,
    value: value,
    onChange: e => !disabled && onChange && onChange(e.target.value, e)
  }, props), options && options.map(attributes => /*#__PURE__*/React.createElement("option", _extends({
    id: ids.option(attributes.key),
    key: attributes.key,
    value: attributes.key
  }, attributes), attributes.label)));
  return /*#__PURE__*/React.createElement(View, {
    style: rootStyle
  }, /*#__PURE__*/React.createElement(View, _extends({
    id: ids.root
  }, UNSAFE_containerProps, {
    style: containerStyle,
    onClick: e => disabled && onDisabledClick && onDisabledClick(e),
    tag: "label",
    htmlFor: ids.select || name,
    accessibilityLabel: accessibilityLabel || (selectedOption ? "".concat(label, ": ").concat(selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label) : label)
  }), /*#__PURE__*/React.createElement(View, {
    id: ids.background,
    style: backgroundStyle
  }), /*#__PURE__*/React.createElement(View, {
    id: ids.textWrapper,
    style: textWrapperStyle
  }, label && label !== '' && /*#__PURE__*/React.createElement(LabelParticle, {
    labelFeedbackStateIconStyle: labelFeedbackStateIconStyle,
    error: error,
    warning: warning,
    hasValue: hasValue,
    id: ids.label,
    viewStyle: labelViewStyle,
    textStyle: labelTextStyle
  }, label), selectedOption && /*#__PURE__*/React.createElement(View, {
    id: ids.selectedOption,
    style: selectedOptionStyle
  }, selectedOption.label)), selectElement, loading && /*#__PURE__*/React.createElement(View, {
    id: ids.loader,
    style: loaderStyle
  }, /*#__PURE__*/React.createElement(Loader, null)), !loading && !fieldTooltip && /*#__PURE__*/React.createElement(View, {
    id: ids.arrowWrapper,
    style: arrowStyle
  }, /*#__PURE__*/React.createElement(ActionIconChevronBottom, _extends({
    id: ids.arrow
  }, iconStyle))), fieldTooltip && /*#__PURE__*/React.createElement(FieldTooltipParticle, {
    id: ids.fieldTooltip,
    arrow: arrow,
    hasFluidWidth: fieldTooltipFluidWidth,
    ariaLabel: fieldTooltipAriaLabel,
    disabled: disabled,
    error: error,
    fieldTooltip: fieldTooltip,
    onActive: onActive,
    warning: warning
  })));
};
export default compose(setDisplayName('BorderlessDropdown'), setPropTypes({
  accessibilityLabel: PropTypes.string,
  accessibilityRole: PropTypes.string,
  selectRef: PropTypes.func,
  UNSAFE_containerProps: PropTypes.object,
  UNSAFE_selectProps: PropTypes.object
}), baseDropdown, withUncontrolledProp({
  prop: 'tooltipActive',
  handlers: {
    onActive: props => isActive => props.tooltipActive = isActive
  }
}), withDesignVersion, withDesignContext(_objectSpread(_objectSpread({}, defaultColorVariables), defaultTextStyles)), wrapOverridableComponent('BorderlessDropdown', getDefaultStyleSheet), omitProps('accessible', 'accessibilityRole', 'autofill', 'designContext', 'designVersion', 'id', 'position', 'size', 'stack', 'tooltipActive', 'width'), withDeprecationWarning({
  name: 'BorderlessDropdown',
  removalDate: 'v6.0.0',
  useInstead: 'Dropdown'
}))(BorderlessDropdown);
//# sourceMappingURL=index.js.map